/**
 * Trade Category Select Component
 *
 * A searchable combobox for selecting a primary trade from the enhanced trade_categories table.
 * Uses Popover + Command components for efficient search through 50+ trades.
 * Trades are grouped by section for easier navigation.
 *
 * Updated to use the new useTrades hook with the enhanced trade_categories schema
 * that includes section, display_order, and is_section_header fields.
 */

import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Check, ChevronsUpDown, Briefcase } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTrades, Trade, groupTradesBySection } from '@/hooks/useTrades';

// Re-export Trade as TradeCategory for backwards compatibility
export type TradeCategory = Trade;

interface TradeCategorySelectProps {
  value?: Trade | null;
  onChange: (trade: Trade) => void;
  disabled?: boolean;
  showLabel?: boolean;
}

export function TradeCategorySelect({ value, onChange, disabled = false, showLabel = true }: TradeCategorySelectProps) {
  const [open, setOpen] = React.useState(false);
  const { data: allTrades, isLoading, error: queryError, refetch } = useTrades();

  // Group trades by section for the dropdown display
  const groupedTrades = React.useMemo(() => groupTradesBySection(allTrades || []), [allTrades]);

  const handleSelect = (trade: Trade) => {
    if (!disabled) {
      onChange(trade);
      setOpen(false);
    }
  };

  const error = queryError ? 'Unable to load trade categories. Please try again.' : null;

  if (error) {
    return (
      <div className="space-y-3">
        {showLabel && (
          <label className="text-sm font-medium text-gray-700">
            Select Your Primary Trade
          </label>
        )}
        <div className="rounded-md border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-600">{error}</p>
          <Button
            onClick={() => refetch()}
            variant="outline"
            size="sm"
            className="mt-2"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {showLabel && <Skeleton className="h-5 w-32" />}
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {showLabel && (
        <label className="text-sm font-medium text-gray-700">
          Select Your Primary Trade
        </label>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "w-full justify-between h-auto min-h-[44px] py-2 px-3",
              !value && "text-muted-foreground"
            )}
          >
            {value ? (
              <div className="flex items-center gap-2 text-left">
                <div className="w-7 h-7 rounded-md bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-foreground truncate">{value.name}</div>
                  {value.section && (
                    <div className="text-xs text-muted-foreground truncate">{value.section}</div>
                  )}
                </div>
              </div>
            ) : (
              <span>Search and select a trade...</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command
            filter={(value, search) => {
              if (!search) return 1;
              return value.toLowerCase().includes(search.toLowerCase().trim()) ? 1 : 0;
            }}
          >
            <CommandInput placeholder="Search trades..." className="h-10" />
            <CommandList className="max-h-[300px]">
              <CommandEmpty>No trade found.</CommandEmpty>
              {groupedTrades.map((section) => (
                <CommandGroup key={section.section || 'Other'} heading={section.section || 'Other'}>
                  {section.trades.map((trade) => (
                    <CommandItem
                      key={trade.id}
                      value={`${trade.name} ${trade.section || ''}`}
                      onSelect={() => handleSelect(trade)}
                      className="flex items-center gap-2 py-2"
                    >
                      <div className={cn(
                        "w-6 h-6 rounded flex items-center justify-center flex-shrink-0",
                        value?.id === trade.id ? "bg-blue-500" : "bg-gray-100"
                      )}>
                        <Briefcase className={cn(
                          "w-3.5 h-3.5",
                          value?.id === trade.id ? "text-white" : "text-gray-600"
                        )} />
                      </div>
                      <span className="flex-1 truncate">{trade.name}</span>
                      <Check
                        className={cn(
                          "h-4 w-4 flex-shrink-0",
                          value?.id === trade.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {!value && !isLoading && (
        <p className="text-xs text-gray-500 italic">
          Select your primary trade to help build your partner network
        </p>
      )}
    </div>
  );
}
